"use client"

import { ChartLine, DiscordLogo, GithubLogo, Globe, Wrench, X, XLogo } from "@phosphor-icons/react"
import { Chart } from "@tokenterminal/core/feature/chart/Chart"
import { ChartContextProvider } from "@tokenterminal/core/feature/chart/tt-ui-chart/ChartContext"
import { IconLinkButton } from "@tokenterminal/core/ui/button/IconLinkButton"
import { Avatar } from "@tokenterminal/core/ui/icon/Avatar"
import { Icon } from "@tokenterminal/core/ui/icon/Icon"
import { Card } from "@tokenterminal/core/ui/layout/card/Card"
import { Badge } from "@tokenterminal/core/ui/messaging/badge/Badge"
import { Tooltip } from "@tokenterminal/core/ui/overlays/tooltip/Tooltip"
import { Skeleton } from "@tokenterminal/core/ui/progress/skeleton/Skeleton"
import { chartColors } from "@tokenterminal/core/ui/theme/chart-colors"
import { getLatestPriceLabel } from "@tokenterminal/core/utils/numerals"
import { formatNumber } from "@tokenterminal/core/utils/sharedFormatter"
import type { CustomChart } from "@tokenterminal/tt-analytics-api-types/dist/api/customChart"
import { atom } from "jotai"
import { useMemo } from "react"
import { FADE_MASK_STYLE } from "@/utils/masonryMask"
import { useProjectMetricsData } from "./hooks/useProjectMetricsData"

// Helper to convert slug to ID (hyphens to underscores)
function _toId(slug: string): string {
  return slug.replace(/-/g, "_")
}

function getBadgeColor(decimalChange: number): "green" | "red" | "neutral" {
  if (decimalChange > 0) return "green"
  if (decimalChange < 0) return "red"
  return "neutral"
}

function getBadgePreface(decimalChange: number): string {
  if (decimalChange > 0) return "+"
  if (decimalChange < 0) return "-"
  return ""
}

function getMetricValue(
  metricConfig: { static?: boolean },
  data: { avg?: number; latest?: number },
): number {
  if (metricConfig.static) {
    return data.latest || data.avg || 0
  }
  return data.avg || 0
}

interface ProjectMetricsSectionProps {
  className?: string
}

export function ProjectMetricsSection({ className }: ProjectMetricsSectionProps = {}) {
  const selectedProject = "solana"

  // Use custom hook for all data fetching
  const {
    metricsConfig,
    metricsConfigById,
    metricsConfigBySlug,
    selectedProjectData,
    selectedProjectConfig,
    keyMetricIds,
    keyMetricBreakdown24h,
    keyMetricBreakdown30d,
    metric1LegendData,
    metric2LegendData,
    isLoading,
    METRIC1_SLUG,
  } = useProjectMetricsData(selectedProject)

  // Build key metrics display data
  const keyMetrics = useMemo(() => {
    if (!keyMetricIds || !metricsConfig.length) return []
    return keyMetricIds
      .map((metricId: string) => {
        const metricConfig = metricsConfigById.get(metricId)
        const data24h = keyMetricBreakdown24h[metricId]
        const data30d = keyMetricBreakdown30d[metricId]
        if (!metricConfig || !data24h || !data30d) return null

        const isStatic = metricConfig.static
        return {
          metric_id: metricId,
          label: isStatic ? metricConfig.title : `${metricConfig.title} (30d)`,
          value: isStatic
            ? data24h.latest || data24h.avg || 0
            : getMetricValue(metricConfig, data30d),
          change: isStatic ? data30d.change : data30d.trend,
          slug: metricConfig.slug,
          prefix: metricConfig.format === "price" ? "$" : "",
          metricFormat: metricConfig.format,
        }
      })
      .filter(Boolean) as Array<{
      metric_id: string
      label: string
      value: number
      change: number
      slug: string
      prefix: string
      metricFormat?: string
    }>
  }, [
    keyMetricIds,
    metricsConfigById,
    keyMetricBreakdown24h,
    keyMetricBreakdown30d,
    metricsConfig.length,
  ])

  // Create chart config with two metrics
  const chartConfigAtom = useMemo(() => {
    if (!selectedProjectData || !selectedProjectConfig) {
      return atom<CustomChart>({
        id: "placeholder",
        title: "",
        description: null,
        configs: [],
        zoom: "365d",
        granularity: "week",
        show_title: false,
        show_description: false,
        show_table: false,
      })
    }

    const metric1Config = metricsConfigBySlug.get(METRIC1_SLUG)
    const metric2Config = metricsConfigBySlug.get(selectedProjectConfig.metric2)

    if (!metric1Config || !metric2Config) {
      return atom<CustomChart>({
        id: "placeholder",
        title: "",
        description: null,
        configs: [],
        zoom: "365d",
        granularity: "week",
        show_title: false,
        show_description: false,
        show_table: false,
      })
    }

    // Determine y-axis placement
    const metric2Axis = metric1Config.format === metric2Config.format ? "left" : "right"

    const config: CustomChart = {
      id: `project-metrics-${selectedProject}`,
      title: `${selectedProjectData.name}: ${metric1Config.title} vs ${metric2Config.title}`,
      description: null,
      configs: [
        {
          id: "metric1",
          yAxis: "left",
          title: metric1Config.title,
          colors: [],
          metric: METRIC1_SLUG,
          display: null,
          filters: [
            {
              type: "project",
              values: [selectedProjectData.data_id],
              comparator: "in",
            },
          ],
          groupBy: "project",
          projects: [],
          chart_type: "line",
          bridged: false,
        },
        {
          id: "metric2",
          yAxis: metric2Axis,
          title: metric2Config.title,
          colors: [],
          metric: selectedProjectConfig.metric2,
          display: null,
          filters: [
            {
              type: "project",
              values: [selectedProjectData.data_id],
              comparator: "in",
            },
          ],
          groupBy: "project",
          projects: [],
          chart_type: "line",
          bridged: false,
        },
      ],
      zoom: "365d",
      granularity: "week",
      show_title: false,
      show_description: false,
      show_table: false,
    }

    return atom(config)
  }, [selectedProjectData, selectedProjectConfig, metricsConfigBySlug, METRIC1_SLUG])

  const metric1Config = metricsConfigBySlug.get(METRIC1_SLUG)
  const metric2Config = selectedProjectConfig
    ? metricsConfigBySlug.get(selectedProjectConfig.metric2)
    : null

  return (
    <div
      className="border-t border-l border-solid border-border-default rounded-tl-xl px-8 py-8"
      style={FADE_MASK_STYLE}
    >
      <div className="flex flex-col md:flex-row gap-8 transform scale-90 lg:scale-[0.85] origin-top-left">
        {/* Left Column: Project Lockup and Key Metrics */}
        <div className="w-full md:w-[320px] flex-shrink-0">
          {/* Project Lockup */}
          <div className="mb-6">
            {isLoading || !selectedProjectData ? (
              <div className="flex items-center gap-3 mb-2">
                <Skeleton className="w-10 h-10 rounded-full shrink-0" />
                <div className="flex flex-col flex-1 min-w-0 gap-2">
                  <Skeleton className="h-6 w-32" />
                  <Skeleton className="h-4 w-24" />
                </div>
              </div>
            ) : (
              <>
                {/* Project Name, Logo, Symbol */}
                <div className="flex items-center gap-3 mb-2">
                  <Avatar
                    size="small"
                    src={`https://static1.tokenterminal.com/${selectedProjectData.logo}`}
                    className="rounded-full overflow-hidden shrink-0 w-10 h-10"
                  />
                  <div className="flex flex-col flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="text-xl font-medium text-fg-default truncate">
                        {selectedProjectData.name}
                      </h3>
                      {selectedProjectData.symbol && (
                        <span
                          className="text-fg-secondary text-sm font-normal flex-shrink-0"
                          style={{
                            fontFamily: "var(--font-geist-mono)",
                          }}
                        >
                          {selectedProjectData.symbol}
                        </span>
                      )}
                    </div>
                    {/* Market Sector */}
                    {selectedProjectData.market_sectors &&
                      selectedProjectData.market_sectors.length > 0 && (
                        <div className="text-sm text-fg-secondary">
                          {selectedProjectData.market_sectors[0].name}
                        </div>
                      )}
                  </div>
                </div>
                {/* Price */}
                {selectedProjectData.price_data?.latest?.value && (
                  <div className="flex flex-col gap-1 mt-4">
                    <div className="flex items-center gap-2 text-sm text-fg-secondary font-medium">
                      Price
                      <div className="bg-emerald-500 dark:bg-emerald-400 w-1.5 h-1.5 rounded-full" />
                    </div>
                    <div
                      className="text-2xl font-medium text-fg-default"
                      style={{
                        fontFamily: "var(--font-geist-mono)",
                      }}
                    >
                      {(() => {
                        const priceValue = selectedProjectData.price_data.latest.value
                        const { formattedPrice } = getLatestPriceLabel(priceValue)
                        return formattedPrice.replace("~", "")
                      })()}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <label className="tt-field-label text-fg-secondary mb-3">Key metrics</label>
          {isLoading ? (
            Array.from({ length: 5 }).map((_, i) => (
              <div
                key={i}
                className="grid grid-cols-[1fr_auto_auto] gap-4 py-2 border-t border-solid border-border-default last-of-type:border-b"
              >
                <Skeleton className="h-[20px] w-1/3 group-odd:w-1/4 rounded-md" />
                <Skeleton className="h-[20px] w-[80px] rounded-md" />
                <Skeleton className="h-[20px] w-[60px] rounded-md" />
              </div>
            ))
          ) : (
            <div className="grid grid-cols-[1fr_auto_auto]">
              {keyMetrics.map((metric) => {
                const badgeColor = getBadgeColor(metric.change)
                return (
                  <div key={metric.metric_id} className="contents">
                    <div className="py-2 pr-2 border-solid border-b border-border-default flex items-center">
                      <span className="text-fg-default text-sm">{metric.label}</span>
                    </div>
                    <div className="p-2 border-solid border-b border-border-default flex items-center justify-end">
                      <span className="text-fg-secondary text-sm text-right">
                        {formatNumber({
                          value: metric.value,
                          abbreviationFormat: "compact",
                          metricFormat: metric.metricFormat,
                          prefix: metric.prefix,
                        })}
                      </span>
                    </div>
                    <div className="flex items-center justify-start py-2 border-solid border-b border-border-default">
                      <Tooltip content="30 day change" tooltipProps={{ placement: "right" }}>
                        <div className="cursor-default flex">
                          <Badge
                            color={badgeColor}
                            variant="outline"
                            size="small"
                            className={`!py-0 !gap-0 tabular-nums tracking-wider ${
                              badgeColor === "neutral"
                                ? "!text-neutral-600 dark:!text-neutral-400"
                                : ""
                            }`}
                          >
                            <span className="mr-px">{getBadgePreface(metric.change)}</span>
                            {Math.abs(metric.change * 100).toFixed(1)}%
                          </Badge>
                        </div>
                      </Tooltip>
                    </div>
                  </div>
                )
              })}
            </div>
          )}
          {/* Project Description */}
          {selectedProjectData?.description_what && (
            <div className="mt-6">
              <p className="text-sm text-fg-secondary leading-relaxed">
                {selectedProjectData.description_what}
              </p>
            </div>
          )}
          {/* Social Links */}
          {selectedProjectData?.links && selectedProjectData.links.length > 0 && (
            <div className="mt-6 flex flex-row gap-2">
              {selectedProjectData.links.map((link: { title: string; url: string }) => {
                const linkTitle = link.title.toLowerCase()
                let icon
                let label = link.title

                // Map link titles to icons
                if (linkTitle === "twitter" || linkTitle === "x") {
                  icon = XLogo
                  label = "Twitter"
                } else if (linkTitle === "github") {
                  icon = GithubLogo
                  label = "GitHub"
                } else if (linkTitle === "discord") {
                  icon = DiscordLogo
                  label = "Discord"
                } else if (linkTitle === "website") {
                  icon = Globe
                  label = "Website"
                } else if (linkTitle === "governance" || linkTitle === "docs") {
                  icon = Wrench
                  label = link.title
                } else if (
                  linkTitle === "explorer" ||
                  linkTitle === "analytics" ||
                  linkTitle === "dashboard"
                ) {
                  icon = ChartLine
                  label = link.title
                } else {
                  // Default icon for unknown links
                  icon = Globe
                }

                return (
                  <IconLinkButton
                    key={link.url}
                    icon={icon}
                    href={link.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    variant="muted"
                    size="small"
                    className="!w-6 !h-6"
                    label={label}
                    aria-label={`View ${selectedProjectData.name} ${label} link`}
                  />
                )
              })}
            </div>
          )}
        </div>

        {/* Right Column: Comparison Chart */}
        <div className="w-[1200px] flex flex-col gap-4 min-w-0">
          <Card className="rounded-2xl flex flex-col p-6 w-[1200px] overflow-hidden relative">
            {isLoading || !selectedProjectData ? (
              <div className="h-[400px] flex items-center justify-center">
                <Skeleton className="w-full h-full" style={{ animation: "none" }} />
              </div>
            ) : (
              <>
                {/* Chart Title */}
                <div className="mb-4 h-7 flex items-center">
                  <h3 className="text-lg tracking-tight font-medium">
                    {metric1Config && metric2Config
                      ? `${metric1Config.title} vs ${metric2Config.title}`
                      : "Chart"}
                  </h3>
                </div>

                {/* Chart Legends */}
                <div className="flex flex-col md:flex-row gap-4 mt-1 -mb-4 overflow-x-auto md:w-full w-[calc(100vw-theme(spacing.9))] px-6 md:px-0 -ml-6 md:ml-0 min-h-[80px]">
                  {metric1Config && metric2Config ? (
                    <div className="flex flex-row gap-4 flex-wrap">
                      {/* Metric 1 Legend */}
                      <div className="flex items-start gap-3 px-4 py-3">
                        <div
                          className="w-3 h-3 rounded-full mt-1 flex-shrink-0"
                          style={{ backgroundColor: chartColors["1"] }}
                        />
                        <div className="flex flex-col gap-0.5">
                          <span className="text-sm text-fg-secondary">{metric1Config.title}</span>
                          <span
                            className="text-2xl font-medium text-fg-default"
                            style={{
                              fontFamily: "var(--font-geist-mono)",
                            }}
                          >
                            {metric1LegendData
                              ? formatNumber({
                                  value: metric1LegendData.latest || metric1LegendData.avg || 0,
                                  abbreviationFormat: "compact",
                                  metricFormat: metric1Config.format,
                                  prefix: metric1Config.format === "price" ? "$" : "",
                                })
                              : "-"}
                          </span>
                          <span className="text-xs text-fg-secondary">Latest</span>
                        </div>
                      </div>

                      {/* Metric 2 Legend */}
                      {metric2Config && (
                        <div className="relative flex items-start gap-3 px-4 py-3 border border-dashed border-border-default rounded-lg">
                          <div
                            className="w-3 h-3 rounded-full mt-1 flex-shrink-0"
                            style={{
                              backgroundColor: chartColors["2"],
                            }}
                          />
                          <div className="flex flex-col gap-0.5 pr-6">
                            <span className="text-sm text-fg-secondary">{metric2Config.title}</span>
                            <span
                              className="text-2xl font-medium text-fg-default"
                              style={{
                                fontFamily: "var(--font-geist-mono)",
                              }}
                            >
                              {metric2LegendData
                                ? formatNumber({
                                    value: metric2LegendData.latest || metric2LegendData.avg || 0,
                                    abbreviationFormat: "compact",
                                    metricFormat: metric2Config.format,
                                    prefix: metric2Config.format === "price" ? "$" : "",
                                  })
                                : "-"}
                            </span>
                            <span className="text-xs text-fg-secondary">1y sum</span>
                          </div>
                          <Icon
                            as={X}
                            className="absolute top-3 right-3 w-4 h-4 text-fg-secondary"
                          />
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-row gap-4 flex-wrap">
                      <Skeleton className="h-16 w-48" />
                      <Skeleton className="h-16 w-48" />
                    </div>
                  )}
                </div>

                {/* Chart */}
                <div className="h-[400px] mt-8 w-[800px] relative">
                  <ChartContextProvider>
                    <Chart
                      configAtom={chartConfigAtom}
                      showTitle={false}
                      showDescription={false}
                      showLegend={false}
                      isVisible
                      hideYAxisLabels={false}
                    />
                  </ChartContextProvider>
                </div>
              </>
            )}
          </Card>
        </div>
      </div>
    </div>
  )
}
